import { FrequencyPattern } from '../services/adminService'
import { Button } from './Button'
import { useTranslation } from '../i18n/hooks/useTranslation'

interface FrequencyBuilderProps {
  value: FrequencyPattern
  onChange: (frequency: FrequencyPattern) => void
}

const getDaysOfWeek = (t: any) => [
  { value: 1, label: t('frequencyBuilder.days.mon') },
  { value: 2, label: t('frequencyBuilder.days.tue') },
  { value: 3, label: t('frequencyBuilder.days.wed') },
  { value: 4, label: t('frequencyBuilder.days.thu') },
  { value: 5, label: t('frequencyBuilder.days.fri') },
  { value: 6, label: t('frequencyBuilder.days.sat') },
  { value: 0, label: t('frequencyBuilder.days.sun') },
]

export function FrequencyBuilder({ value, onChange }: FrequencyBuilderProps) {
  const { t } = useTranslation()
  const DAYS_OF_WEEK = getDaysOfWeek(t)

  const handleModeChange = (newMode: FrequencyPattern['mode']) => {
    const basePattern: FrequencyPattern = {
      mode: newMode,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }

    switch (newMode) {
      case 'daily':
        onChange({
          ...basePattern,
          times: value.times && value.times.length > 0 ? value.times : ['09:00'],
        })
        break
      case 'hourly':
        onChange({
          ...basePattern,
          startHour: 9,
          endHour: 18,
        })
        break
      case 'weekly':
        onChange({
          ...basePattern,
          daysOfWeek: [1], // Monday
          times: ['09:00'],
        })
        break
      case 'monthly':
        onChange({
          ...basePattern,
          times: ['09:00'],
        })
        break
      default:
        onChange(basePattern)
    }
  }

  const handleTimeChange = (index: number, time: string) => {
    const newTimes = [...(value.times || [])]
    newTimes[index] = time
    onChange({ ...value, times: newTimes })
  }

  const addTime = () => {
    const newTimes = [...(value.times || []), '12:00']
    onChange({ ...value, times: newTimes })
  }

  const removeTime = (index: number) => {
    const newTimes = value.times?.filter((_, i) => i !== index) || []
    onChange({ ...value, times: newTimes })
  }

  const toggleDay = (day: number) => {
    const currentDays = value.daysOfWeek || []
    const newDays = currentDays.includes(day)
      ? currentDays.filter(d => d !== day)
      : [...currentDays, day].sort()
    onChange({ ...value, daysOfWeek: newDays })
  }

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('frequencyBuilder.frequencyType')}
        </label>
        <div className="grid grid-cols-4 gap-2">
          <button
            type="button"
            onClick={() => handleModeChange('hourly')}
            className={`p-3 border rounded-full text-sm transition-colors ${
              value.mode === 'hourly'
                ? 'border-primary-500 bg-primary-50 text-primary-700'
                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            {t('frequencyBuilder.hourly')}
          </button>
          <button
            type="button"
            onClick={() => handleModeChange('daily')}
            className={`p-3 border rounded-full text-sm transition-colors ${
              value.mode === 'daily'
                ? 'border-primary-500 bg-primary-50 text-primary-700'
                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            {t('frequencyBuilder.daily')}
          </button>
          <button
            type="button"
            onClick={() => handleModeChange('weekly')}
            className={`p-3 border rounded-full text-sm transition-colors ${
              value.mode === 'weekly'
                ? 'border-primary-500 bg-primary-50 text-primary-700'
                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            {t('frequencyBuilder.weekly')}
          </button>
          <button
            type="button"
            onClick={() => handleModeChange('monthly')}
            className={`p-3 border rounded-full text-sm transition-colors ${
              value.mode === 'monthly'
                ? 'border-primary-500 bg-primary-50 text-primary-700'
                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            {t('frequencyBuilder.monthly')}
          </button>
        </div>
      </div>

      {value.mode === 'daily' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('frequencyBuilder.times')}
          </label>
          <div className="space-y-2">
            {value.times?.map((time, index) => (
              <div key={index} className="flex items-center gap-2">
                <input
                  type="time"
                  value={time}
                  onChange={(e) => handleTimeChange(index, e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-full bg-white text-gray-700 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                {(value.times?.length || 0) > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeTime(index)}
                  >
                    {t('frequencyBuilder.remove')}
                  </Button>
                )}
              </div>
            ))}
            <Button type="button" variant="secondary" size="sm" onClick={addTime}>
              {t('frequencyBuilder.addTime')}
            </Button>
          </div>
        </div>
      )}

      {value.mode === 'hourly' && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('frequencyBuilder.startHour')}
            </label>
            <select
              value={value.startHour || 9}
              onChange={(e) => onChange({ ...value, startHour: parseInt(e.target.value) })}
              className="w-full pl-3 pr-8 py-2 border border-gray-300 rounded-full bg-white text-gray-700 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              {Array.from({ length: 24 }, (_, i) => (
                <option key={i} value={i}>
                  {i.toString().padStart(2, '0')}:00
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('frequencyBuilder.endHour')}
            </label>
            <select
              value={value.endHour || 18}
              onChange={(e) => onChange({ ...value, endHour: parseInt(e.target.value) })}
              className="w-full pl-3 pr-8 py-2 border border-gray-300 rounded-full bg-white text-gray-700 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              {Array.from({ length: 24 }, (_, i) => i + 1).map((i) => (
                <option key={i} value={i}>
                  {i.toString().padStart(2, '0')}:00
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {value.mode === 'weekly' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('frequencyBuilder.daysOfWeek')}
          </label>
          <div className="flex gap-1 mb-4">
            {DAYS_OF_WEEK.map((day) => (
              <button
                key={day.value}
                type="button"
                onClick={() => toggleDay(day.value)}
                className={`px-3 py-2 rounded-full text-sm transition-colors ${
                  value.daysOfWeek?.includes(day.value)
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {day.label}
              </button>
            ))}
          </div>
          
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('frequencyBuilder.times')}
          </label>
          <div className="space-y-2">
            {value.times?.map((time, index) => (
              <div key={index} className="flex items-center gap-2">
                <input
                  type="time"
                  value={time}
                  onChange={(e) => handleTimeChange(index, e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-full bg-white text-gray-700 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                {(value.times?.length || 0) > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeTime(index)}
                  >
                    {t('frequencyBuilder.remove')}
                  </Button>
                )}
              </div>
            ))}
            <Button type="button" variant="secondary" size="sm" onClick={addTime}>
              {t('frequencyBuilder.addTime')}
            </Button>
          </div>
        </div>
      )}

      {value.mode === 'monthly' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('frequencyBuilder.times')}
          </label>
          <div className="space-y-2">
            {value.times?.map((time, index) => (
              <div key={index} className="flex items-center gap-2">
                <input
                  type="time"
                  value={time}
                  onChange={(e) => handleTimeChange(index, e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-full bg-white text-gray-700 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                {(value.times?.length || 0) > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeTime(index)}
                  >
                    {t('frequencyBuilder.remove')}
                  </Button>
                )}
              </div>
            ))}
            <Button type="button" variant="secondary" size="sm" onClick={addTime}>
              {t('frequencyBuilder.addTime')}
            </Button>
          </div>
        </div>
      )}

      {/* Preview */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-700 mb-2">{t('frequencyBuilder.previewTitle')}</h4>
        <div className="text-sm text-gray-600">
          {value.mode === 'daily' && (
            <p>{t('frequencyBuilder.preview.daily', { times: value.times?.join(', ') || t('frequencyBuilder.preview.noTimes') })}</p>
          )}
          {value.mode === 'hourly' && (
            <p>{t('frequencyBuilder.preview.hourly', { startHour: value.startHour || 9, endHour: value.endHour || 18 })}</p>
          )}
          {value.mode === 'weekly' && (
            <p>
              {value.daysOfWeek?.length ? 
                t('frequencyBuilder.preview.weekly', {
                  days: DAYS_OF_WEEK.filter(d => value.daysOfWeek?.includes(d.value)).map(d => d.label).join(', '),
                  times: value.times?.join(', ') || t('frequencyBuilder.preview.noTimes')
                }) :
                t('frequencyBuilder.preview.noDays')
              }
            </p>
          )}
          {value.mode === 'monthly' && (
            <p>{t('frequencyBuilder.preview.monthly', { times: value.times?.join(', ') || t('frequencyBuilder.preview.noTimes') })}</p>
          )}
        </div>
      </div>
    </div>
  )
} 